/**
 * The customer's estimate approval page: /estimate/:id/approve?t=token
 *
 * They were sent a link; this gives them something to say yes to. Approving
 * hands them straight to the deposit payment page while they are still in the
 * moment — which is the whole point of asking at approval rather than after a
 * phone call three days later.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, FileText, AlertCircle, ShieldCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '../lib/api';

const inr = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ApproveEstimate = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [fatal, setFatal] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/public/estimate/${id}/approval`,
        { params: { t: token } });
      setData(d);
    } catch (e) {
      setFatal(e?.response?.data?.detail || 'This estimate link is not valid.');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setApproving(true); setErr('');
    try {
      const { data: d } = await axios.post(`${API}/public/estimate/${id}/approve`, null,
        { params: { t: token } });
      setData(d);
      // Straight to the deposit — not a dead-end thank-you screen.
      if (d.pay_url) window.location.href = d.pay_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not record your approval. Please try again.');
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={26} />
      </div>
    );
  }

  if (fatal) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
        <div className="max-w-sm text-center" data-testid="approve-error">
          <AlertCircle className="mx-auto text-slate-400" size={30} />
          <p className="mt-3 text-slate-700 font-medium">{fatal}</p>
          <p className="mt-1 text-sm text-slate-500">Please ask the workshop for a fresh link.</p>
        </div>
      </div>
    );
  }

  const accent = data.brand_color || '#0d9488';
  const approved = data.status === 'accepted' || data.approved;
  const deposit = Number(data.deposit_requested || 0);
  const depositDue = Math.max(0, deposit - Number(data.deposit_paid || 0));
  // Before approval there is no ask yet, but the policy says what is coming —
  // nobody should meet the deposit for the first time after committing.
  const pct = Number(data.deposit_percent_default || 0);
  const expectedDeposit = deposit > 0
    ? deposit
    : (pct > 0 && data.accepts_online ? Math.round(Number(data.total || 0) * pct) / 100 : 0);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8" data-testid="approve-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          {data.has_logo && data.workshop_slug ? (
            <img
              src={`${API}/public/workshop/${data.workshop_slug}/logo`}
              alt={data.workshop_name}
              className="h-14 mx-auto object-contain"
            />
          ) : (
            <div className="text-lg font-semibold" style={{ color: accent }}>{data.workshop_name}</div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="text-xs uppercase tracking-wider text-slate-400">Estimate</div>
            <div className="text-base font-semibold text-slate-900" data-testid="approve-number">
              {data.number}
            </div>
            {data.subject && <div className="text-sm text-slate-500 mt-0.5">{data.subject}</div>}
          </div>

          <div className="px-6 py-6">
            <div className="text-xs uppercase tracking-wider text-slate-400">Quoted</div>
            <div className="text-4xl font-semibold tracking-tight text-slate-900 mt-1" data-testid="approve-total">
              ₹ {inr(data.total)}
            </div>
            <div className="text-xs text-slate-500 mt-1.5">
              {data.item_count} item{data.item_count === 1 ? '' : 's'} · dated {data.date}
            </div>

            <a
              href={data.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium"
              style={{ color: accent }}
              data-testid="approve-view-pdf"
            >
              <FileText size={14} /> Read the full estimate
            </a>

            {approved ? (
              <div className="mt-6 text-center py-2" data-testid="approve-done">
                <CheckCircle2 className="mx-auto" size={34} style={{ color: accent }} />
                <div className="mt-2 text-base font-semibold text-slate-900">Approved — thank you</div>
                {depositDue > 0 ? (
                  <>
                    <p className="text-sm text-slate-500 mt-1">
                      Please pay the ₹ {inr(depositDue)} deposit to confirm your slot.
                    </p>
                    {!data.pay_url && data.upi_uri && (
                      <div className="mt-4 flex flex-col items-center gap-3" data-testid="approve-upi">
                        <div className="p-2 bg-white border border-slate-200 rounded-lg"><QRCodeSVG value={data.upi_uri} size={150} level="M" /></div>
                        <a href={data.upi_uri} className="inline-block w-full text-white text-base font-semibold py-3.5 rounded-xl" style={{ backgroundColor: accent }} data-testid="approve-pay-upi-btn">
                          Pay ₹ {inr(depositDue)} with a UPI app
                        </a>
                        <p className="text-xs text-slate-500">Or scan the QR. UPI ID <span className="font-mono">{data.upi_vpa}</span>. {data.workshop_name} will confirm once it's received.</p>
                      </div>
                    )}
                    {data.pay_url && (
                      <a
                        href={data.pay_url}
                        className="mt-4 inline-block w-full text-white text-base font-semibold py-3.5 rounded-xl"
                        style={{ backgroundColor: accent }}
                        data-testid="approve-pay-deposit-btn"
                      >
                        Pay ₹ {inr(depositDue)} deposit
                      </a>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-500 mt-1">
                    {data.workshop_name} has been notified and will be in touch about
                    scheduling your job.
                  </p>
                )}
              </div>
            ) : (
              <>
                {expectedDeposit > 0 && (
                  <div className="mt-5 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5" data-testid="approve-deposit-note">
                    A deposit of <b>₹ {inr(expectedDeposit)}</b> is payable on approval to
                    confirm the booking. It is adjusted against your final invoice.
                  </div>
                )}
                {err && (
                  <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="approve-err">
                    {err}
                  </div>
                )}
                <button
                  onClick={approve}
                  disabled={approving}
                  className="mt-5 w-full text-white text-base font-semibold py-3.5 rounded-xl disabled:opacity-50"
                  style={{ backgroundColor: accent }}
                  data-testid="approve-btn"
                >
                  {approving
                    ? <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Recording your approval…</span>
                    : (depositDue > 0 && (data.pay_url || data.upi_uri) ? `Approve & pay ₹ ${inr(depositDue)}` : 'Approve this estimate')}
                </button>
                <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
                  <ShieldCheck size={13} />
                  Approving confirms the quoted price and lets work be scheduled
                </div>
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-5 text-[11px] text-slate-400">
          Quoted by {data.workshop_name}
          {data.workshop_phone ? ` · ${data.workshop_phone}` : ''}
        </div>
      </div>
    </div>
  );
};

export default ApproveEstimate;
